/**
 * Adapted from PlayCanvas Engine's framework/parsers/sog.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import {
  GSplatSogData,
  type GSplatSogFiles,
  type GSplatSogMeta,
  type GSplatSogSources,
  type GSplatSogTextureSource,
} from '@/plugins/gsplat/core/GSplatSogData'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type {
  GSplatHandler,
  GSplatLoadUrlObject,
  GSplatParserContext,
  IGSplatParser,
} from '@/plugins/gsplat/loaders/GSplatHandler'
import { GSplatResource } from '@/plugins/gsplat/unified/resource/GSplatResource'
import { GSplatSogResource } from '@/plugins/gsplat/unified/resource/GSplatSogResource'

export interface DecodedSogTexture {
  readonly width: number
  readonly height: number
  readonly pixels: Uint8Array | Uint8ClampedArray
}

export interface SogTextureDecodeContext {
  readonly filename: string
  readonly sourceName: keyof GSplatSogSources
  readonly signal?: AbortSignal
}

export type SogTextureDecoder = (
  blob: Blob,
  context: SogTextureDecodeContext
) => Promise<DecodedSogTexture>

export interface SogParserOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly meta?: unknown
  readonly mapUrl?: (filename: string) => string
  readonly progress?: (loadedBytes: number, totalBytes: number) => void
  readonly loadSHN?: boolean
  readonly withCredentials?: boolean
  readonly decompress?: boolean
  readonly prepareCenters?: boolean
  /** metadata 给出可信数量后、请求 texture 与分配 GPU 资源前执行。 */
  readonly validateSplatCount?: (count: number) => void
  readonly decodeTexture?: SogTextureDecoder
  readonly onData?: (data: GSplatSogData) => void
}

export type SogResource = GSplatSogResource<GSplatSogData> | GSplatResource

interface RawRecord {
  readonly [key: string]: unknown
}

interface SogSourceRequest {
  readonly sourceName: keyof GSplatSogSources
  readonly filename: string
}

export interface SogResourceBuildInput {
  readonly meta: unknown
  readonly url: string
  readonly options: SogParserOptions
  readonly signal?: AbortSignal
  readonly resolveTexture: (filename: string, signal?: AbortSignal) => Promise<Blob>
}

/** 冻结上游 v1 shape 到 canonical v1 count 的 metadata 升级。 */
export function normalizeSogMeta(input: unknown, loadSHN = true): GSplatSogMeta {
  const raw = requireRecord(input, 'meta')
  const means = requireRecord(raw.means, 'means')
  const legacyShape = Array.isArray(means.shape) ? means.shape : null
  if (raw.version !== undefined && raw.version !== 1 && raw.version !== 2) {
    throw new Error(`SogParser: 不支持 metadata version=${String(raw.version)}`)
  }
  if (raw.version === undefined && !legacyShape) {
    throw new Error('SogParser: v1 metadata 缺少 means.shape')
  }
  const version = raw.version === 2 ? 2 : 1
  const countValue = legacyShape ? legacyShape[0] : raw.count
  if (!Number.isSafeInteger(countValue) || Number(countValue) < 0) {
    throw new Error(`SogParser: 非法 splat count=${String(countValue)}`)
  }
  const count = Number(countValue)
  const quats = requireRecord(raw.quats, 'quats')
  const scales = requireRecord(raw.scales, 'scales')
  const sh0 = requireRecord(raw.sh0, 'sh0')
  const shN = raw.shN === undefined ? undefined : requireRecord(raw.shN, 'shN')
  const base = {
    count,
    means: {
      files: stringArray(means.files, 'means.files'),
      mins: numberTuple3(means.mins, 'means.mins'),
      maxs: numberTuple3(means.maxs, 'means.maxs'),
    },
    quats: { files: stringArray(quats.files, 'quats.files') },
  }
  if (version === 2) {
    return {
      ...base,
      version,
      scales: {
        files: stringArray(scales.files, 'scales.files'),
        codebook: codebookArray(scales.codebook, 'scales.codebook'),
      },
      sh0: {
        files: stringArray(sh0.files, 'sh0.files'),
        codebook: codebookArray(sh0.codebook, 'sh0.codebook'),
      },
      ...(loadSHN && shN
        ? {
            shN: {
              files: stringArray(shN.files, 'shN.files'),
              codebook: codebookArray(shN.codebook, 'shN.codebook'),
              ...(numberOrUndefined(shN.bands, 'shN.bands') !== undefined
                ? { bands: numberOrUndefined(shN.bands, 'shN.bands') }
                : {}),
              ...(numberOrUndefined(shN.count, 'shN.count') !== undefined
                ? { count: numberOrUndefined(shN.count, 'shN.count') }
                : {}),
            },
          }
        : {}),
    }
  }
  return {
    ...base,
    version,
    scales: {
      files: stringArray(scales.files, 'scales.files'),
      mins: numberTuple3(scales.mins, 'scales.mins'),
      maxs: numberTuple3(scales.maxs, 'scales.maxs'),
    },
    sh0: {
      files: stringArray(sh0.files, 'sh0.files'),
      mins: numberTuple4(sh0.mins, 'sh0.mins'),
      maxs: numberTuple4(sh0.maxs, 'sh0.maxs'),
    },
    ...(loadSHN && shN
      ? {
          shN: {
            files: stringArray(shN.files, 'shN.files'),
            mins: finiteNumber(shN.mins, 'shN.mins'),
            maxs: finiteNumber(shN.maxs, 'shN.maxs'),
          },
        }
      : {}),
  }
}

/** JSON 与 bundle 共用的唯一 SOG Data/Resource publication path。 */
export async function buildSogResource(input: SogResourceBuildInput): Promise<SogResource> {
  const { options, signal } = input
  throwIfAborted(signal)
  const meta = normalizeSogMeta(input.meta, options.loadSHN ?? true)
  options.validateSplatCount?.(meta.count)
  if (meta.version === 2) {
    options.adapter.validateTextureDescriptor({
      label: input.url ? `sogCodebook_${input.url}` : 'sogCodebook',
      width: 256,
      height: 1,
      format: 'rgba32float',
      usages: ['sampled', 'copy-src', 'copy-dst'],
      minFilter: 'nearest',
      magFilter: 'nearest',
      addressU: 'clamp-to-edge',
      addressV: 'clamp-to-edge',
    })
  }
  const requests = sourceRequests(meta)
  const created = new Set<GSplatSogTextureSource['texture']>()
  const resolved = new Map<keyof GSplatSogSources, GSplatSogTextureSource>()
  const decoder = options.decodeTexture ?? decodeSogTexture
  const results = await Promise.allSettled(
    requests.map(async ({ filename, sourceName }) => {
      const blob = await input.resolveTexture(filename, signal)
      throwIfAborted(signal)
      const decoded = await decoder(blob, { filename, sourceName, signal })
      throwIfAborted(signal)
      return { sourceName, decoded }
    })
  )
  const rejected = results.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  )
  if (rejected) {
    throw rejected.reason
  }
  try {
    throwIfAborted(signal)
    const decodedSources = results.map((result) => {
      if (result.status !== 'fulfilled') throw result.reason
      return result.value
    })
    // SOG 的全部 source descriptor 先通过，再创建第一张 payload texture。
    for (const { sourceName, decoded } of decodedSources) {
      preflightTextureSource(options.adapter, sourceName, decoded)
    }
    for (const { sourceName, decoded } of decodedSources) {
      const source = createTextureSource(options.adapter, sourceName, decoded)
      created.add(source.texture)
      resolved.set(sourceName, source)
    }
  } catch (error) {
    for (const texture of created) texture.dispose()
    throw error
  }
  const sources = asSources(resolved)
  let data: GSplatSogData | null = null
  let ownershipTransferred = false
  try {
    data = new GSplatSogData({ adapter: options.adapter, meta, sources, url: input.url })
    options.onData?.(data)
    if (options.decompress) {
      const uncompressed = await data.decompress(signal)
      throwIfAborted(signal)
      const resource = GSplatResource.create(options.adapter, uncompressed, {
        prepareCenters: options.prepareCenters,
      })
      data.destroy()
      data = null
      return resource
    }

    data.prepareCodebook()
    if (options.prepareCenters ?? true) await data.generateCenters(signal)
    throwIfAborted(signal)
    const resource = GSplatSogResource.create(options.adapter, data, {
      prepareCenters: options.prepareCenters ?? true,
    })
    ownershipTransferred = true
    return resource
  } catch (error) {
    if (!ownershipTransferred) {
      if (data) data.destroy()
      else for (const texture of created) texture.dispose()
    }
    throw error
  }
}

export async function decodeSogTexture(blob: Blob): Promise<DecodedSogTexture> {
  if (typeof createImageBitmap !== 'function') {
    throw new Error('SogParser: 当前环境不支持 createImageBitmap，请注入 decodeTexture')
  }
  const bitmap = await createImageBitmap(blob, {
    colorSpaceConversion: 'none',
    premultiplyAlpha: 'none',
  })
  try {
    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(bitmap.width, bitmap.height)
        : createHtmlCanvas(bitmap.width, bitmap.height)
    const context = canvas.getContext('2d', { willReadFrequently: true }) as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null
    if (!context) throw new Error('SogParser: 无法创建 2D canvas context')
    context.drawImage(bitmap, 0, 0)
    const image = context.getImageData(0, 0, bitmap.width, bitmap.height)
    return { width: bitmap.width, height: bitmap.height, pixels: image.data }
  } finally {
    bitmap.close()
  }
}

export class SogParser implements IGSplatParser<SogResource, SogParserOptions> {
  handler?: GSplatHandler<SogResource, SogParserOptions> | null

  canParse(context: GSplatParserContext<SogParserOptions>): boolean {
    return context.ext === 'json'
  }

  async load(
    url: GSplatLoadUrlObject,
    context: GSplatParserContext<SogParserOptions>
  ): Promise<SogResource> {
    const options = context.options
    if (!options?.adapter) throw new Error('SogParser: 缺少 GSplatGraphicsAdapter')
    const maxRetries = this.handler?.maxRetries ?? 0
    const meta = options.meta ?? (await loadJson(url.load, options, context.signal, maxRetries))
    const progress = new ProgressAggregator(options.progress)
    return buildSogResource({
      meta,
      url: url.original,
      options,
      signal: context.signal,
      resolveTexture: async (filename, signal) => {
        const mapped =
          options.mapUrl?.(filename) ?? resolveRelativeUrl(filename, url.load, url.original)
        return loadBlob(mapped, options, signal, progress, maxRetries)
      },
    })
  }
}

class ProgressAggregator {
  private readonly entries = new Map<string, Readonly<{ loaded: number; total: number }>>()

  constructor(private readonly callback?: (loaded: number, total: number) => void) {}

  update(key: string, loaded: number, total: number): void {
    if (!this.callback) return
    this.entries.set(key, { loaded, total })
    let sumLoaded = 0
    let sumTotal = 0
    for (const entry of this.entries.values()) {
      sumLoaded += entry.loaded
      sumTotal += entry.total
    }
    this.callback(sumLoaded, sumTotal)
  }
}

async function loadJson(
  source: string | Blob,
  options: SogParserOptions,
  signal?: AbortSignal,
  maxRetries = 0
): Promise<unknown> {
  throwIfAborted(signal)
  if (source instanceof Blob) {
    const result: unknown = JSON.parse(await source.text())
    throwIfAborted(signal)
    return result
  }
  const response = await fetchWithRetries(source, fetchInit(options, signal), maxRetries, signal)
  if (!response.ok) {
    throw new Error(`SogParser: metadata 加载失败 ${response.status} ${response.statusText}`)
  }
  const result: unknown = await response.json()
  throwIfAborted(signal)
  return result
}

async function loadBlob(
  url: string,
  options: Pick<SogParserOptions, 'withCredentials'>,
  signal: AbortSignal | undefined,
  progress: ProgressAggregator,
  maxRetries: number
): Promise<Blob> {
  throwIfAborted(signal)
  const response = await fetchWithRetries(url, fetchInit(options, signal), maxRetries, signal)
  if (!response.ok) throw new Error(`SogParser: texture 加载失败 ${response.status} ${url}`)
  const total = Number.parseInt(response.headers.get('content-length') ?? '0', 10)
  if (!response.body) {
    const blob = await response.blob()
    progress.update(url, blob.size, total || blob.size)
    return blob
  }
  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let loaded = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      loaded += value.byteLength
      progress.update(url, loaded, total)
      throwIfAborted(signal)
    }
  } finally {
    reader.releaseLock()
  }
  return new Blob([joinBytes(chunks)])
}

function createTextureSource(
  adapter: GSplatGraphicsAdapter,
  sourceName: keyof GSplatSogSources,
  decoded: DecodedSogTexture
): GSplatSogTextureSource {
  validateDecodedTexture(sourceName, decoded)
  const pixels = new Uint8Array(
    decoded.pixels.buffer,
    decoded.pixels.byteOffset,
    decoded.pixels.byteLength
  ).slice()
  const texture = adapter.createTexture(createTextureSourceDescriptor(sourceName, decoded))
  try {
    adapter.updateTexture(texture, pixels)
    return { texture, pixels }
  } catch (error) {
    texture.dispose()
    throw error
  }
}

function preflightTextureSource(
  adapter: GSplatGraphicsAdapter,
  sourceName: keyof GSplatSogSources,
  decoded: DecodedSogTexture
): void {
  validateDecodedTexture(sourceName, decoded)
  adapter.validateTextureDescriptor(createTextureSourceDescriptor(sourceName, decoded))
}

function validateDecodedTexture(
  sourceName: keyof GSplatSogSources,
  decoded: DecodedSogTexture
): void {
  if (
    !Number.isSafeInteger(decoded.width) ||
    !Number.isSafeInteger(decoded.height) ||
    decoded.width < 1 ||
    decoded.height < 1 ||
    decoded.pixels.byteLength !== decoded.width * decoded.height * 4
  ) {
    throw new Error(`SogParser: ${sourceName} decoded texture 尺寸或 RGBA8 数据非法`)
  }
}

function createTextureSourceDescriptor(
  sourceName: keyof GSplatSogSources,
  decoded: DecodedSogTexture
) {
  return {
    label: sourceName,
    width: decoded.width,
    height: decoded.height,
    format: 'rgba8unorm' as const,
    usages: ['sampled', 'copy-src', 'copy-dst'] as const,
  }
}

function sourceRequests(meta: GSplatSogMeta): readonly SogSourceRequest[] {
  const exact = (name: string, files: GSplatSogFiles, count: number): readonly string[] => {
    if (files.files.length !== count) {
      throw new Error(`SogParser: ${name}.files 数量必须为 ${count}，actual=${files.files.length}`)
    }
    return files.files
  }
  const means = exact('means', meta.means, 2)
  const requests: SogSourceRequest[] = [
    { sourceName: 'means_l', filename: means[0] },
    { sourceName: 'means_u', filename: means[1] },
    { sourceName: 'quats', filename: exact('quats', meta.quats, 1)[0] },
    { sourceName: 'scales', filename: exact('scales', meta.scales, 1)[0] },
    { sourceName: 'sh0', filename: exact('sh0', meta.sh0, 1)[0] },
  ]
  if (meta.shN) {
    const sh = exact('shN', meta.shN, 2)
    requests.push({ sourceName: 'sh_centroids', filename: sh[0] })
    requests.push({ sourceName: 'sh_labels', filename: sh[1] })
  }
  return requests
}

function asSources(
  resolved: ReadonlyMap<keyof GSplatSogSources, GSplatSogTextureSource>
): GSplatSogSources {
  const required = (name: keyof GSplatSogSources): GSplatSogTextureSource => {
    const value = resolved.get(name)
    if (!value) throw new Error(`SogParser: 缺少 decoded source ${name}`)
    return value
  }
  return {
    means_l: required('means_l'),
    means_u: required('means_u'),
    quats: required('quats'),
    scales: required('scales'),
    sh0: required('sh0'),
    sh_labels: resolved.get('sh_labels'),
    sh_centroids: resolved.get('sh_centroids'),
  }
}

function fetchInit(
  options: Pick<SogParserOptions, 'withCredentials'>,
  signal?: AbortSignal
): RequestInit | undefined {
  if (!options.withCredentials && !signal) return undefined
  return {
    ...(options.withCredentials ? { credentials: 'include' as const } : {}),
    ...(signal ? { signal } : {}),
  }
}

async function fetchWithRetries(
  url: string,
  init: RequestInit | undefined,
  maxRetries: number,
  signal?: AbortSignal
): Promise<Response> {
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    throwIfAborted(signal)
    try {
      const response = await fetch(url, init)
      if (response.ok || attempt === maxRetries) return response
      await response.body?.cancel().catch(() => undefined)
    } catch (error) {
      lastError = error
      if (signal?.aborted || attempt === maxRetries) throw error
    }
  }
  throw lastError ?? new Error(`SogParser: fetch 失败 ${url}`)
}

function resolveRelativeUrl(filename: string, load: string | Blob, original: string): string {
  const parent = typeof load === 'string' ? load : original
  try {
    const documentBase = globalThis.document?.baseURI ?? globalThis.location?.href
    const base = documentBase ? new URL(parent, documentBase) : new URL(parent)
    return new URL(filename, base).toString()
  } catch {
    return filename
  }
}

function createHtmlCanvas(width: number, height: number): HTMLCanvasElement {
  if (!globalThis.document) throw new Error('SogParser: 当前环境不支持 canvas')
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

function joinBytes(chunks: readonly Uint8Array[]): ArrayBuffer {
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0)
  const result = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.byteLength
  }
  return result.buffer
}

function requireRecord(value: unknown, name: string): RawRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`SogParser: ${name} 必须为 object`)
  }
  return value as RawRecord
}

function stringArray(value: unknown, name: string): string[] {
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string' || !entry)) {
    throw new Error(`SogParser: ${name} 必须为非空字符串数组`)
  }
  return [...value]
}

function numberTuple3(value: unknown, name: string): [number, number, number] {
  const result = numberArray(value, name, 3)
  return [result[0], result[1], result[2]]
}

function numberTuple4(value: unknown, name: string): [number, number, number, number] {
  const result = numberArray(value, name, 4)
  return [result[0], result[1], result[2], result[3]]
}

function numberArray(value: unknown, name: string, length: number): number[] {
  if (!Array.isArray(value) || value.length !== length) {
    throw new Error(`SogParser: ${name} 必须包含 ${length} 个数值`)
  }
  return value.map((entry, index) => finiteNumber(entry, `${name}[${index}]`))
}

function codebookArray(value: unknown, name: string): Array<number | null> {
  if (!Array.isArray(value) || value.length !== 256) {
    throw new Error(`SogParser: ${name} 必须包含 256 项`)
  }
  return value.map((entry, index) => {
    if (index === 0 && entry === null) return null
    return finiteNumber(entry, `${name}[${index}]`)
  })
}

function finiteNumber(value: unknown, name: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`SogParser: ${name} 必须为有限数值`)
  }
  return value
}

function numberOrUndefined(value: unknown, name: string): number | undefined {
  return value === undefined ? undefined : finiteNumber(value, name)
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('SogParser load 已取消', 'AbortError')
}
